import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/api.service';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Pagination
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

interface CallItem {
  id: number;
  date: string;
  manager_name: string;
  project_name: string;
  customer_name: string;
  phone: string;
  mp3_url?: string;
  lead_id?: number;
}

interface QualityAssessment {
  id?: number;
  call_id?: number;
  audio_url?: string | null;
  transcript?: string | null;
  model_name?: string | null;
  sale_probability: number;
  sale_probability_explanation: string;
  conversation_outcome: string;
  conversation_summary: string;
  client_mood_score: number;
  client_mood_explanation: string;
  client_facts: string[];
  client_needs: string;
  client_objections: string;
  manager_politeness_score: number;
  manager_politeness_explanation: string;
  manager_presentation: string;
  objection_handling_score: number;
  objection_handling_explanation: string;
  created_at?: string;
}

export const QualityControlPage: React.FC = () => {
  const [calls, setCalls] = useState<CallItem[]>([]);
  const [assessmentsByCallId, setAssessmentsByCallId] = useState<Record<number, QualityAssessment>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [playingCallId, setPlayingCallId] = useState<number | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCall, setDetailsCall] = useState<CallItem | null>(null);
  const [detailsAssessment, setDetailsAssessment] = useState<QualityAssessment | null>(null);

  const [onlyWithAssessment, setOnlyWithAssessment] = useState<boolean>(true);
  const [searchPhone, setSearchPhone] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchPhone, onlyWithAssessment]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: offset.toString()
      });

      // Добавляем фильтры поиска
      if (searchPhone) {
        params.append('phone', searchPhone);
      }

      if (onlyWithAssessment) {
        params.append('has_assessment', 'true');
      }

      const res = await apiService.get(`/calls?${params.toString()}`);
      const responseData = res.data || {};
      const items: CallItem[] = responseData.data || [];
      
      setCalls(items);
      setTotalItems(responseData.pagination?.total || 0);
      setTotalPages(responseData.pagination?.totalPages || 0);
      
      // Fetch assessments in background for those items
      fetchAssessmentsForCalls(items);
    } catch (e: any) {
      setError('Не удалось загрузить список звонков');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentsForCalls = async (items: CallItem[]) => {
    // simple concurrency control
    const concurrency = 8;
    let index = 0;

    const runNext = async () => {
      if (index >= items.length) return;
      const batch = items.slice(index, index + concurrency);
      index += concurrency;
      await Promise.all(batch.map(async (c) => {
        try {
          const resp = await apiService.get(`/quality-control/assessments/by-call/${c.id}`);
          if (resp && resp.status === 200 && resp.data) {
            setAssessmentsByCallId(prev => ({ ...prev, [c.id]: normalizeAssessment(resp.data) }));
          }
        } catch (err: any) {
          // 404 means no assessment — ignore silently
        }
      }));
      await runNext();
    };

    await runNext();
  };

  const normalizeAssessment = (raw: any): QualityAssessment => {
    return {
      sale_probability: toNumber(raw.sale_probability, 0),
      sale_probability_explanation: raw.sale_probability_explanation || '',
      conversation_outcome: raw.conversation_outcome || '',
      conversation_summary: raw.conversation_summary || '',
      client_mood_score: toNumber(raw.client_mood_score, 0),
      client_mood_explanation: raw.client_mood_explanation || '',
      client_facts: Array.isArray(raw.client_facts) ? raw.client_facts : safeParseArray(raw.client_facts),
      client_needs: raw.client_needs || '',
      client_objections: raw.client_objections || '',
      manager_politeness_score: toNumber(raw.manager_politeness_score, 0),
      manager_politeness_explanation: raw.manager_politeness_explanation || '',
      manager_presentation: raw.manager_presentation || '',
      objection_handling_score: toNumber(raw.objection_handling_score, 0),
      objection_handling_explanation: raw.objection_handling_explanation || '',
      audio_url: raw.audio_url ?? null,
      call_id: raw.call_id,
      transcript: raw.transcript ?? null,
      model_name: raw.model_name ?? null,
      created_at: raw.created_at,
    };
  };

  const safeParseArray = (val: any): string[] => {
    if (!val) return [];
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const toNumber = (v: any, def: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const playAudio = (mp3Url: string, callId: number) => {
    if (audioElement) {
      audioElement.pause();
    }
    const audio = new Audio(mp3Url);
    audio.playbackRate = playbackSpeed;
    audio.play();
    setPlayingCallId(callId);
    setAudioElement(audio);
    audio.onended = () => {
      setPlayingCallId(null);
      setAudioElement(null);
    };
  };

  const stopAudio = () => {
    if (audioElement) {
      audioElement.pause();
      setPlayingCallId(null);
      setAudioElement(null);
    }
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
    if (audioElement && playingCallId) {
      audioElement.playbackRate = newSpeed;
    }
  };

  const openDetails = (call: CallItem) => {
    const a = assessmentsByCallId[call.id];
    setDetailsCall(call);
    setDetailsAssessment(a || null);
    setDetailsOpen(true);
  };

  const filtered = useMemo(() => {
    return calls;
  }, [calls]);

  // Пагинация - используем данные с сервера
  const paginatedCalls = useMemo(() => {
    return filtered;
  }, [filtered]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // Сброс на первую страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [searchPhone, onlyWithAssessment]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Проверка качества
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
        <Stack direction="row" spacing={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
          <Button variant="contained" onClick={loadData} disabled={loading} sx={{ flex: 1 }}>
            {loading ? '...' : 'Обновить'}
          </Button>
          <FormControl size="small" sx={{ width: 80 }}>
            <Select
              value={playbackSpeed}
              onChange={(e) => handleSpeedChange(e.target.value as number)}
              displayEmpty
            >
              <MenuItem value={1}>1x</MenuItem>
              <MenuItem value={1.25}>1.25x</MenuItem>
              <MenuItem value={1.5}>1.5x</MenuItem>
              <MenuItem value={2}>2x</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Button 
          variant={onlyWithAssessment ? 'contained' : 'outlined'} 
          onClick={() => setOnlyWithAssessment(v => !v)}
          fullWidth={isMobile}
        >
          {onlyWithAssessment ? 'Только с оценкой' : 'Все звонки'}
        </Button>
        <TextField
          label="Поиск по телефону"
          size="small"
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
          fullWidth={isMobile}
        />
      </Box>

      <Card>
        <CardContent sx={{ p: isMobile ? 1 : 2 }}>
          <Typography variant="h6" gutterBottom>
            Результаты ({totalItems})
          </Typography>

          {paginatedCalls.length === 0 ? (
            <Typography variant="body1" sx={{ py: 3, textAlign: 'center' }}>
              Данных нет
            </Typography>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {paginatedCalls.map(call => {
                const a = assessmentsByCallId[call.id];
                return (
                  <Box key={call.id} sx={{ p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(call.date).toLocaleString()}
                      </Typography>
                      {call.mp3_url && (
                        <IconButton onClick={() => playingCallId === call.id ? stopAudio() : playAudio(call.mp3_url!, call.id)} color="primary" size="small" sx={{ p: 0 }}>
                          {playingCallId === call.id ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{call.phone}</Typography>
                    <Typography variant="caption" display="block">{call.manager_name} / {call.project_name}</Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}>
                      {a ? (
                        <>
                          <Chip label={a.conversation_outcome || '—'} size="small" sx={{ height: 20, fontSize: 10 }} />
                          <Chip label={`Вероятн: ${a.sale_probability}%`} size="small" color={a.sale_probability >= 60 ? 'success' : a.sale_probability >= 30 ? 'warning' : 'default'} sx={{ height: 20, fontSize: 10 }} />
                          <Button variant="outlined" size="small" onClick={() => openDetails(call)} sx={{ fontSize: 10, py: 0, height: 20 }}>
                            Детали
                          </Button>
                        </>
                      ) : (
                        <Chip label="Нет оценки" size="small" color="default" sx={{ height: 20, fontSize: 10 }} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr 0.6fr 0.6fr', gap: 1, alignItems: 'center', fontSize: 14, color: 'text.secondary' }}>
              <Box sx={{ fontWeight: 600 }}>Дата</Box>
              <Box sx={{ fontWeight: 600 }}>Менеджер / Проект</Box>
              <Box sx={{ fontWeight: 600 }}>Клиент</Box>
              <Box sx={{ fontWeight: 600 }}>Телефон</Box>
              <Box sx={{ fontWeight: 600 }}>Итог</Box>
              <Box sx={{ fontWeight: 600 }}>Вероятность</Box>
              <Box sx={{ fontWeight: 600 }}>Настроение</Box>
              <Box sx={{ fontWeight: 600 }}>Возражения</Box>
              <Box sx={{ fontWeight: 600 }}>Аудио</Box>
              <Box sx={{ fontWeight: 600 }}>Детали</Box>

              {paginatedCalls.map(call => {
                const a = assessmentsByCallId[call.id];
                return (
                  <React.Fragment key={call.id}>
                    <Box>{new Date(call.date).toLocaleString()}</Box>
                    <Box>
                      <Box sx={{ fontWeight: 600 }}>{call.manager_name || '-'}</Box>
                      <Box>{call.project_name || '-'}</Box>
                    </Box>
                    <Box>{call.customer_name || '-'}</Box>
                    <Box>{call.phone || '-'}</Box>
                    <Box>
                      {a ? (
                        <Chip label={a.conversation_outcome || '—'} size="small" />
                      ) : (
                        <Chip label="Нет оценки" size="small" color="default" />
                      )}
                    </Box>
                    <Box>
                      {a ? (
                        <Tooltip title={a.sale_probability_explanation}>
                          <Chip label={`${a.sale_probability}%`} size="small" color={a.sale_probability >= 60 ? 'success' : a.sale_probability >= 30 ? 'warning' : 'default'} />
                        </Tooltip>
                      ) : '—'}
                    </Box>
                    <Box>
                      {a ? (
                        <Tooltip title={a.client_mood_explanation}>
                          <Chip label={`${a.client_mood_score}`} size="small" color={a.client_mood_score >= 60 ? 'success' : a.client_mood_score >= 30 ? 'warning' : 'default'} />
                        </Tooltip>
                      ) : '—'}
                    </Box>
                    <Box>
                      {a ? (
                        <Tooltip title={a.objection_handling_explanation}>
                          <Chip label={`${a.objection_handling_score}`} size="small" color={a.objection_handling_score >= 60 ? 'success' : a.objection_handling_score >= 30 ? 'warning' : 'default'} />
                        </Tooltip>
                      ) : '—'}
                    </Box>
                    <Box>
                      {call.mp3_url ? (
                        <IconButton onClick={() => playingCallId === call.id ? stopAudio() : playAudio(call.mp3_url!, call.id)} color="primary" size="small">
                          {playingCallId === call.id ? <StopIcon /> : <PlayArrowIcon />}
                        </IconButton>
                      ) : '—'}
                    </Box>
                    <Box>
                      <Button variant="outlined" size="small" onClick={() => openDetails(call)} disabled={!a}>
                        Подробнее
                      </Button>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Пагинация */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      )}

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pr: 6 }}>
          Детальная информация
          {isMobile && (
            <IconButton onClick={() => setDetailsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: isMobile ? 1 : 2 }}>
          {!detailsAssessment ? (
            <Typography>Оценка не найдена</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Итоги</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 0.5, fontSize: 13 }}>
                    <Box>Вероятность продажи</Box>
                    <Box sx={{ fontWeight: 'bold' }}>{detailsAssessment.sale_probability}%</Box>
                    <Box>Настроение клиента</Box>
                    <Box sx={{ fontWeight: 'bold' }}>{detailsAssessment.client_mood_score}</Box>
                    <Box>Вежливость менеджера</Box>
                    <Box sx={{ fontWeight: 'bold' }}>{detailsAssessment.manager_politeness_score}</Box>
                    <Box>Отработка возражений</Box>
                    <Box sx={{ fontWeight: 'bold' }}>{detailsAssessment.objection_handling_score}</Box>
                    <Box>Итог разговора</Box>
                    <Box sx={{ fontWeight: 'bold' }}>{detailsAssessment.conversation_outcome || '—'}</Box>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Ключевые факты</Typography>
                  {detailsAssessment.client_facts?.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                      {detailsAssessment.client_facts.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Краткое содержание</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap" sx={{ fontSize: 13 }}>{detailsAssessment.conversation_summary || '—'}</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Потребности</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap" sx={{ fontSize: 13 }}>{detailsAssessment.client_needs || '—'}</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Возражения</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap" sx={{ fontSize: 13 }}>{detailsAssessment.client_objections || '—'}</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Объяснение вероятности</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap" sx={{ fontSize: 13 }}>{detailsAssessment.sale_probability_explanation || '—'}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detailsCall?.mp3_url && (
            <Button size="small" onClick={() => playingCallId === detailsCall!.id ? stopAudio() : playAudio(detailsCall!.mp3_url!, detailsCall!.id)}>
              {playingCallId === detailsCall?.id ? 'Стоп' : 'Слушать'}
            </Button>
          )}
          <Button size="small" onClick={() => setDetailsOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QualityControlPage; 